import type { EnrichmentError } from 'state/common-form/infos/infos.model';
import { getFormMetadataByFormId } from 'state/forms/metadata/selectors';
import { getRejections } from 'state/forms/rejections/selectors';
import { hasStreamingPriceByTabId } from 'state/quotes/streaming/streaming.selector';
import type { PriceEnhancementMode, TradeQuoteAction } from 'state/quotes/trades/trades.model';
import { isLiveQuote, isPastQuote } from 'state/quotes/utils';
import { getFormRequestType } from 'state/selectors';
import { getFormQuoteType, getUserDefaultProductView } from 'state/selectors/common';
import { getProductVariation } from 'state/selectors/common/misc';
import {
  getStaticFormInfos,
  getStaticQuoteRequestStartingTimeInMillis,
  getStaticQuoteResult,
  getStaticQuoteState,
  getStaticTechnicalError,
} from 'state/selectors/common/staticForm';
import { getTradeInfosByWorkspaceTab } from 'state/selectors/common/temp';
import {
  mapTradeAuthorizationStatusToWorkflowStatus,
  mapTradeStatusToWorkflowStatus,
} from 'state/selectors/meta/quote/utils';
import type { AppState } from 'state/state.model';

export function getStaticFormPriceEnhancementMode(state: AppState, tabId: string): PriceEnhancementMode {
  const infos = getStaticFormInfos(state, tabId);
  if (!infos) {
    return 'None';
  }
  const tradeAction = infos.actions.find(x => x.name === 'TradableQuote' || x.name === 'ExecOnly') as TradeQuoteAction;
  if (!tradeAction) {
    return 'None';
  }
  return tradeAction.priceEnhancement;
}

export function isStaticFormLoaded(state: AppState, tabId: string): boolean {
  if (!getFormMetadataByFormId(state, 'static', tabId)) {
    return false;
  }
  const infos = getStaticFormInfos(state, tabId);
  if (!infos) {
    return false;
  }
  const productView = getUserDefaultProductView(state);
  if (
    !getProductVariation(state, productView, {
      productSubType: infos.productDescription,
      productType: infos.instrumentName,
    })
  ) {
    return false;
  }
  return true;
}

export function getStaticFormEnrichmentErrorCorrelationId(state: AppState, tabId: string): string | undefined {
  const infos = getStaticFormInfos(state, tabId);
  if (infos?.enrichmentError === undefined) {
    return undefined;
  }
  const enrichmentError = infos.enrichmentError as EnrichmentError;
  return enrichmentError?.correlationId;
}

export function getStaticWorkflowStatus(state: AppState, tabId: string): WorkFlowStatus {
  const formInfos = getStaticFormInfos(state, tabId);
  const quoteRequestTimeInMillis = getStaticQuoteRequestStartingTimeInMillis(state, tabId);
  const technicalError = getStaticTechnicalError(state, tabId);
  const tradeInfos = getTradeInfosByWorkspaceTab(state, 'static', tabId);
  const quoteState = getStaticQuoteState(state, tabId);
  const quoteType = getFormQuoteType(state, 'static', tabId);
  const rejections = getRejections(state, tabId);
  const requestType = getFormRequestType(state, 'static', tabId);

  if (requestType === 'RequestForStream') {
    if (rejections.length > 0 || tradeInfos.tradeStatus === 'Rejected') {
      return 'REJECTED';
    }
    if (technicalError !== undefined) {
      return 'ERROR';
    }

    if (tradeInfos?.tradeStatus !== undefined && isLiveQuote(quoteState)) {
      const status = mapTradeStatusToWorkflowStatus(tradeInfos.tradeStatus);
      if (status === 'TRADED') {
        return 'STREAMING_TRADED';
      }
    }
    if (isPastQuote(quoteState)) {
      return 'STREAM_EXPIRED';
    }

    if (quoteRequestTimeInMillis) {
      if (!hasStreamingPriceByTabId(state, tabId)) {
        return 'STREAMING_INITIATED';
      }
      if (quoteType === 'Tradable') {
        return 'STREAMING_TRADABLE';
      }
      return 'STREAMING';
    }
  }

  if (!formInfos) {
    return 'UNDEFINED';
  }

  if (technicalError !== undefined) {
    return 'ERROR';
  }

  if (quoteRequestTimeInMillis) {
    return 'QUOTING';
  }
  if (tradeInfos?.tradeStatus !== undefined && isLiveQuote(quoteState)) {
    return mapTradeStatusToWorkflowStatus(tradeInfos.tradeStatus);
  }
  const quoteResult = getStaticQuoteResult(state, tabId);
  if (quoteResult) {
    if (quoteResult.status === 'Quoted') {
      if (isPastQuote(quoteState)) {
        return 'QUOTED_PREVIOUSLY';
      }
      return mapTradeAuthorizationStatusToWorkflowStatus(tradeInfos?.tradeAuthStatus);
    } else if (quoteResult.status === 'Rejected' || quoteResult.status === 'Failed') {
      return 'REJECTED';
    }
  }
  if (formInfos.actions?.some(x => !x.isDisabled)) {
    return 'READY';
  }

  return 'INITIALIZED';
}

export type WorkFlowStatus =
  | 'UNDEFINED'
  | 'ERROR'
  | 'LOADING'
  | 'INITIALIZED'
  | 'QUOTED_PREVIOUSLY'
  | 'QUOTED_TRADABLE'
  | 'QUOTED'
  | 'QUOTING'
  | 'STREAMING_INITIATED'
  | 'STREAMING'
  | 'STREAMING_TRADABLE'
  | 'STREAM_EXPIRED'
  | 'READY'
  | 'REJECTED'
  | 'TRADE_AUTH_REJECTED'
  | 'TRADED'
  | 'STREAMING_TRADED'
  | 'TRADING'
  | 'BOOKING'
  | 'BOOKED';
